import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ShieldCheck, FileSearch, Banknote, Lock, Plus, Check, Minus, X,
  Zap, TrendingUp, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandMark } from "@/components/BrandMark";
import { format } from "@/lib/brand-tokens";
import { trustIndicators, deals, brands, getBrandById } from "@/lib/mock-data";

// PUB-01 Home — full-bleed editorial hero with a layered live-activity composition.
// Designed to translate cleanly to dark mode by leaning on semantic tokens (surface, ink)
// and brand-anchored color (brand-700 / brand-900 / mint-300) that work in both palettes.
export default function HomePage() {
  const featured = deals.filter((d) => d.status === "Published").slice(0, 3);
  const ticker = [42, 68, 55, 80, 72, 92, 100];

  return (
    <div className="bg-surface-alt">
      {/* ── HERO ───────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-hero-rich">
        {/* Layered backdrop — grid + grain + ambient glow */}
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-60" />
        <div className="absolute inset-0 grain-texture pointer-events-none opacity-[0.18] mix-blend-multiply" />

        {/* Decorative concentric rings — top-right, slowly rotating */}
        <div className="absolute -top-32 -right-32 hidden lg:block pointer-events-none animate-spin-slow opacity-50">
          <svg width="540" height="540" viewBox="0 0 540 540" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="270" cy="270" r="120" stroke="rgba(15,118,110,0.12)" strokeWidth="1" />
            <circle cx="270" cy="270" r="180" stroke="rgba(15,118,110,0.10)" strokeWidth="1" strokeDasharray="2 8" />
            <circle cx="270" cy="270" r="240" stroke="rgba(15,118,110,0.08)" strokeWidth="1" />
          </svg>
        </div>

        {/* Brand mark — top right */}
        <div className="absolute top-6 right-8 hidden md:block animate-fade-in z-10" style={{ animationDelay: "300ms", animationFillMode: "both" }}>
          <BrandMark id="001" />
        </div>

        <div className="relative max-w-site mx-auto px-6 lg:px-12 pt-16 pb-28 lg:pt-24 lg:pb-36 grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* LEFT — copy column */}
          <div className="lg:col-span-7 relative z-10">
            {/* Live indicator pill */}
            <div className="inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full bg-surface/80 backdrop-blur-sm border border-line shadow-xs" data-testid="hero-live-pill">
              <span className="relative flex">
                <span className="size-2 rounded-full bg-emerald-500" />
                <span className="absolute inset-0 size-2 rounded-full bg-emerald-500 animate-pulse-ring" />
              </span>
              <span className="text-caption font-semibold text-ink uppercase tracking-wider">Live</span>
              <span className="size-1 rounded-full bg-line" />
              <span className="text-caption text-ink-muted">8 deals open right now</span>
            </div>

            <h1 className="mt-7 font-brand font-bold text-ink leading-[1.02] tracking-[-0.025em]
                           text-[44px] sm:text-[60px] lg:text-[80px]">
              <span className="block">Cards in,</span>
              <span className="block">
                <span className="relative inline-block text-brand-700">
                  cash out
                  <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none" preserveAspectRatio="none">
                    <path d="M2 8 Q 80 2, 150 7 T 298 6" stroke="#5EEAD4" strokeWidth="3" strokeLinecap="round" fill="none" />
                  </svg>
                </span>{" "}
                &mdash;
              </span>
              <span className="block">by ACH, in days.</span>
            </h1>

            <p className="mt-8 text-lg text-ink-muted max-w-xl leading-relaxed">
              We pool the unused gift-card supply of vetted members to negotiate
              rates a single seller can&rsquo;t. No reseller chains. No app-store
              fee skim. Just a clean, audited pipe from your inbox to your bank.
            </p>

            {/* CTAs — modernized */}
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/apply" data-testid="hero-apply-btn">
                <Button className="group relative overflow-hidden h-[52px] pl-7 pr-5 rounded-full bg-brand-700 hover:bg-brand-800 text-white text-body-sm font-semibold inline-flex items-center gap-3 shadow-[0_8px_24px_rgba(15,118,110,0.32)] hover:shadow-[0_12px_32px_rgba(15,118,110,0.42)] hover:-translate-y-0.5 transition-all duration-300">
                  <span className="relative z-10">Apply to join</span>
                  <span className="relative z-10 size-7 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45 group-hover:bg-white/25">
                    <ArrowUpRight className="size-4" strokeWidth={2.4} />
                  </span>
                  {/* Inner highlight bevel */}
                  <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
                  {/* Shimmer */}
                  <span className="absolute inset-0 -translate-x-full group-hover:animate-shimmer-x bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none" />
                </Button>
              </Link>
              <Link to="/deals" data-testid="hero-deals-btn">
                <Button variant="outline" className="group h-[52px] pl-6 pr-5 rounded-full border-2 border-ink/15 hover:border-ink hover:bg-transparent text-ink text-body-sm font-semibold inline-flex items-center gap-2 transition-all">
                  See live deals
                  <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
                </Button>
              </Link>
            </div>

            {/* Trust strip with avatar stack */}
            <div className="mt-12 flex items-center gap-5">
              <div className="flex -space-x-2">
                {AVATARS.map((a, i) => (
                  <div key={i} className={`size-9 rounded-full ring-2 ring-surface-alt grid place-items-center text-caption font-bold text-white ${a.bg}`} style={{ zIndex: 10 - i }}>
                    {a.initials}
                  </div>
                ))}
                <div className="size-9 rounded-full ring-2 ring-surface-alt bg-ink grid place-items-center text-caption font-bold text-white">+</div>
              </div>
              <div className="text-caption text-ink-muted leading-snug">
                <span className="text-ink font-semibold">12 new members</span> joined this week
                <div className="text-ink-hint">Closed membership · Verified by Veriff</div>
              </div>
            </div>

            <div className="mt-10 inline-flex items-center gap-2.5 text-caption text-ink-subtle">
              <Lock className="size-3.5" strokeWidth={2.2} />
              KYC-verified · Card numbers and bank accounts encrypted at rest with AWS&nbsp;KMS.
            </div>
          </div>

          {/* RIGHT — layered live-activity composition */}
          <div className="lg:col-span-5 relative">
            <div className="relative h-full min-h-[480px]">
              {/* Floating deal chip — top-left */}
              <FloatingDealChip
                position="top-2 left-0 lg:left-2"
                rotation="-rotate-6"
                delay="200ms"
                brand="HD" name="Home Depot" rate="93" tone="amber"
              />
              {/* Floating deal chip — top-right */}
              <FloatingDealChip
                position="top-12 right-0 lg:-right-4"
                rotation="rotate-6"
                delay="600ms"
                brand="BB" name="Best Buy" rate="91" tone="info"
              />
              {/* Floating deal chip — middle-bottom-left */}
              <FloatingDealChip
                position="bottom-32 left-4"
                rotation="-rotate-3"
                delay="1000ms"
                brand="TGT" name="Target" rate="94" tone="brand"
              />

              {/* Main card + "just paid" badge share a relative wrapper so
                  the badge anchors to the main card's bottom-right, not to
                  the outer container which is much taller due to floating
                  chips and grain overlays. */}
              <div className="relative mt-20 lg:mt-24 mx-auto max-w-[420px]">
                {/* Main "live activity" anchored card — gradient teal, glass-style highlights */}
                <div className="relative z-10 rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(15,118,110,0.45)]">
                {/* Gradient surface */}
                <div className="relative bg-gradient-to-br from-brand-700 via-brand-800 to-brand-950 p-7 text-white">
                  {/* Decorative inner highlight — full-height, very gentle so
                      there's no hard edge between the bright top and the rest
                      of the card. */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/8 via-white/2 to-transparent pointer-events-none" />
                  <div className="absolute inset-0 bg-stripe-dark pointer-events-none" />
                  <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-brand-300/20 blur-2xl pointer-events-none" />

                  <div className="relative">
                    {/* Live header */}
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center gap-2.5">
                        <span className="relative flex">
                          <span className="size-2 rounded-full bg-brand-300" />
                          <span className="absolute inset-0 size-2 rounded-full bg-brand-300 animate-pulse-ring" />
                        </span>
                        <span className="text-eyebrow uppercase text-brand-200 tracking-[0.22em]">Live · 30 days</span>
                      </div>
                      <Sparkles className="size-4 text-brand-300/80" />
                    </div>

                    {/* Headline number */}
                    <div className="mt-5 flex items-baseline gap-2">
                      <span className="font-brand text-[60px] sm:text-[64px] font-bold leading-none tabular-nums tracking-[-0.025em]">
                        {format.currencyCompact(trustIndicators.totalPaidOut)}
                      </span>
                    </div>
                    <div className="mt-1.5 text-body-sm text-brand-100">
                      paid to members &middot; <span className="text-brand-300 font-semibold">+18%</span> MoM
                    </div>

                    {/* Animated bar ticker */}
                    <div className="mt-7">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-caption uppercase tracking-wider text-brand-200/70">Daily payouts</span>
                        <span className="text-caption text-brand-100 inline-flex items-center gap-1">
                          <TrendingUp className="size-3" /> Trending up
                        </span>
                      </div>
                      <div className="grid grid-cols-7 gap-1.5 h-14 items-end">
                        {ticker.map((h, i) => (
                          <div key={i}
                               className="bg-gradient-to-t from-brand-300/40 to-brand-300 rounded-sm origin-bottom animate-ticker-bar"
                               style={{ height: `${h}%`, animationDelay: `${i * 110}ms`, animationDuration: "1.6s" }} />
                        ))}
                      </div>
                    </div>

                    {/* Stat grid */}
                    <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-4">
                      <DarkStat label="Active members" value={format.count(trustIndicators.activeMembers)} />
                      <DarkStat label="Brands live"    value={trustIndicators.brands} />
                      <DarkStat label="Avg rate"       value={`${trustIndicators.averageRate}%`} highlight />
                      <DarkStat label="Cards approved" value={format.count(trustIndicators.approvedLast30d)} />
                    </div>
                  </div>
                </div>
              </div>

              {/* "Just paid" badge — sits just below the main card on the
                  right, with a small gap so they don't visually overlap. */}
              <div className="absolute top-full mt-3 right-2 sm:-right-6 lg:-right-10 z-20 w-[270px] max-w-[80%]">
                <div className="bg-surface border border-line rounded-2xl p-4 pr-5 shadow-xl shadow-ink/10 hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-center gap-3">
                    <div className="relative shrink-0">
                      <div className="size-10 rounded-full bg-gradient-to-br from-brand-300 to-brand-700 grid place-items-center text-white text-caption font-bold ring-2 ring-surface">JW</div>
                      <span className="absolute -bottom-0.5 -right-0.5 size-4 rounded-full bg-emerald-500 ring-2 ring-surface grid place-items-center">
                        <Check className="size-2.5 text-white" strokeWidth={3.5} />
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-body-sm text-ink leading-tight">
                        <span className="font-semibold">Jennifer W.</span> just got paid
                      </div>
                      <div className="font-brand text-body text-brand-700 font-bold tabular-nums">$1,395.00 <span className="text-caption text-ink-subtle font-normal">via ACH</span></div>
                    </div>
                  </div>
                  <div className="mt-2.5 flex items-center gap-1.5 text-caption text-emerald-700 bg-emerald-50 rounded-md px-2 py-1">
                    <Zap className="size-3" strokeWidth={2.4} />
                    <span className="font-semibold">38h</span>
                    <span className="text-emerald-600">from approval</span>
                  </div>
                </div>
              </div>
            </div>  {/* /main-card + jennifer wrapper */}
            </div>
          </div>
        </div>

        {/* Bottom edge fade into next section */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-surface-alt/80 pointer-events-none" />
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────── */}
      <section className="bg-surface border-t border-line">
        <div className="max-w-site mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-3 text-brand-700">How it works</div>
              <h2 className="font-brand text-display text-ink leading-tight tracking-[-0.02em]">
                Three steps from<br />closet card to cash.
              </h2>
              <p className="mt-5 text-body text-ink-muted leading-relaxed max-w-sm">
                We don&rsquo;t auction. We don&rsquo;t list. Members reserve a deal,
                submit cards, and get paid on the published terms. That&rsquo;s the
                whole product.
              </p>
            </div>
            <ol className="lg:col-span-8 grid sm:grid-cols-3 gap-px bg-line border border-line rounded-2xl overflow-hidden">
              <Step n="01" icon={<FileSearch className="size-5" />} title="Reserve a deal" body="Browse live buys. Lock in a face-value range at a published rate before the cap fills." />
              <Step n="02" icon={<ShieldCheck className="size-5" />} title="Submit cards" body="Paste card data. Our parser flags duplicates, format issues, and outliers in real time." />
              <Step n="03" icon={<Banknote className="size-5" />} title="Get paid by ACH" body="Once approved, your payout lands in the bank account on file on the deal&rsquo;s payment terms." />
            </ol>
          </div>
        </div>
      </section>

      {/* ── WHY A BUYING GROUP ─────────────────────────── */}
      <section className="bg-surface-alt border-t border-line">
        <div className="max-w-site mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3 text-brand-700">Why a buying group</div>
            <h2 className="font-brand text-display text-ink leading-tight tracking-[-0.02em]">
              You can&rsquo;t negotiate alone &mdash;<br />
              <span className="text-brand-700">but together, we can.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-line border border-line rounded-2xl overflow-hidden">
            <CompareColumn
              kind="bad"
              label="Solo seller"
              rate="65–75%"
              points={[
                { ok: false, t: "Volume is too small to negotiate" },
                { ok: false, t: "App-store fees skim 8–12%" },
                { ok: false, t: "Anonymous buyer ghosting" },
                { ok: false, t: "No payment terms or recourse" },
              ]}
            />
            <CompareColumn
              kind="meh"
              label="Reseller chain"
              rate="78–86%"
              points={[
                { ok: true,  t: "Slightly better rates" },
                { ok: false, t: "Each middleman takes a cut" },
                { ok: false, t: "Source-of-cards rarely verified" },
                { ok: false, t: "Payout often in store credit, not cash" },
              ]}
            />
            <CompareColumn
              kind="good"
              label="GCBC"
              rate="89–96%"
              points={[
                { ok: true, t: "Pooled volume = wholesale rate" },
                { ok: true, t: "Verified members, verified cards" },
                { ok: true, t: "ACH cash, named payment terms" },
                { ok: true, t: "Audited, encrypted, members-only" },
              ]}
              highlight
            />
          </div>

          <p className="mt-6 text-caption text-ink-subtle max-w-2xl leading-relaxed">
            Rates shown are typical ranges across mainstream brands.
            Your individual rate depends on the brand and the open deal &mdash; see <Link to="/deals" className="text-brand-700 underline">live deals</Link>.
          </p>
        </div>
      </section>

      {/* ── FEATURED DEALS ─────────────────────────────── */}
      <section className="bg-surface border-t border-line">
        <div className="max-w-site mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow mb-3 text-brand-700">Live this week</div>
              <h2 className="font-brand text-display text-ink leading-tight tracking-[-0.02em]">A glimpse at what&rsquo;s open.</h2>
            </div>
            <Link to="/deals" className="hidden sm:inline-flex items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800 group">
              View all live deals
              <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {featured.map((deal) => {
              const brand = getBrandById(deal.brandId);
              const days = Math.max(0, Math.ceil((new Date(deal.deadline) - Date.now()) / 86_400_000));
              const fillPct = Math.round((deal.capUsed / deal.capTotal) * 100);
              const fillCopy = fillPct >= 80 ? "Filling fast" : fillPct >= 40 ? "Healthy availability" : "Plenty available";
              const monogram = brand?.name?.split(" ").map((w) => w[0]).slice(0, 2).join("") || "—";
              const urgent = fillPct >= 80 || days <= 2;
              return (
                <Link
                  to={`/deals/${deal.id}`}
                  key={deal.id}
                  data-testid={`home-deal-card-${deal.id}`}
                  className="group relative overflow-hidden bg-surface border border-line rounded-2xl transition-all hover:shadow-[0_24px_48px_-16px_rgba(15,23,42,0.18)] hover:-translate-y-1 hover:border-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 block"
                >
                  {/* Decorative monogram watermark — large brand initials in the corner */}
                  <div
                    className="absolute -top-6 -right-2 font-brand font-black text-[140px] leading-none tracking-[-0.04em] text-ink/[0.04] select-none pointer-events-none transition-colors group-hover:text-brand-700/[0.06]"
                    aria-hidden="true"
                  >
                    {monogram}
                  </div>

                  {/* Subtle teal corner glow on hover */}
                  <div className="absolute -top-20 -left-20 w-48 h-48 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                       style={{ background: "radial-gradient(closest-side, rgba(15,118,110,0.10), transparent)" }} />

                  <div className="relative p-7">
                    {/* Top row: status pill + days remaining */}
                    <div className="flex items-center justify-between mb-7">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
                        urgent
                          ? "bg-ink text-white"
                          : "bg-brand-50 text-brand-800 border border-brand-100"
                      }`}>
                        <span className={`size-1.5 rounded-full ${urgent ? "bg-brand-300 animate-pulse" : "bg-brand-700"}`} />
                        {urgent ? "Filling fast" : "Open"}
                      </span>
                      <span className="text-caption text-ink-subtle tabular-nums">
                        Closes in <span className="text-ink font-semibold">{days}d</span>
                      </span>
                    </div>

                    {/* Headline: deal name + brand */}
                    <div>
                      <h3 className="font-brand text-h2 text-ink font-bold leading-tight tracking-[-0.015em]">
                        {deal.name}
                      </h3>
                      <div className="mt-1.5 text-caption text-ink-subtle truncate">{brand?.name}</div>
                    </div>

                    {/* Big rate display */}
                    <div className="mt-7 pt-7 border-t border-line/70 flex items-end justify-between gap-4">
                      <div>
                        <div className="text-caption uppercase tracking-wider text-ink-hint">You receive</div>
                        <div className="mt-1 flex items-baseline gap-1">
                          <span className="font-brand text-[44px] font-bold text-brand-700 tabular-nums leading-none tracking-[-0.02em]">{deal.rate}</span>
                          <span className="font-brand text-h2 font-bold text-brand-700 tabular-nums leading-none">%</span>
                        </div>
                        <div className="mt-1 text-caption text-ink-subtle">of face value</div>
                      </div>
                      <div className="text-right">
                        <div className="text-caption uppercase tracking-wider text-ink-hint">Face range</div>
                        <div className="mt-1 font-brand text-h3 text-ink font-semibold tabular-nums leading-none">
                          ${deal.minFace}<span className="text-ink-subtle">–</span>${deal.maxFace}
                        </div>
                      </div>
                    </div>

                    {/* Capacity bar with shimmer */}
                    <div className="mt-7">
                      <div className="flex items-center justify-between text-caption mb-2">
                        <span className="text-ink-muted">{fillCopy}</span>
                        <span className="text-ink-subtle tabular-nums">
                          <span className="text-ink font-semibold">{format.currencyCompact(deal.capUsed)}</span>
                          <span className="mx-1">/</span>
                          {format.currencyCompact(deal.capTotal)}
                          <span className="ml-2 text-ink-hint">·</span>
                          <span className="ml-2 text-ink font-semibold">{fillPct}%</span>
                        </span>
                      </div>
                      <div className="relative h-1.5 rounded-full bg-surface-alt overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-300 rounded-full transition-all duration-700"
                          style={{ width: `${fillPct}%` }}
                        />
                      </div>
                    </div>

                    {/* Action affordance — whole card is the link, this is just the visual cue */}
                    <span className="mt-7 inline-flex items-center gap-1.5 text-body-sm font-semibold text-ink group-hover:text-brand-700 transition-colors">
                      See deal details
                      <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────────
          Light + dark mode aware via surface tokens. Trimmed to 3 archetype
          quotes — corporate / freelance / retail — so the section reads as a
          quick scan instead of a wall of social proof. */}
      <section className="bg-surface-alt border-t border-line">
        <div className="max-w-site mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow text-brand-700 mb-3">Member stories</div>
            <h2 className="font-brand font-bold text-ink leading-[1.05] tracking-[-0.02em] text-[34px] sm:text-[42px]">
              Real numbers.{" "}
              <span className="relative inline-block text-brand-700">
                Real members.
                <svg className="absolute -bottom-1.5 left-0 w-full" height="10" viewBox="0 0 240 10" fill="none" preserveAspectRatio="none">
                  <path d="M2 6 Q 60 1, 120 5 T 238 4" stroke="#5EEAD4" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} data-testid={`testimonial-${i}`}
                      className="group relative bg-surface border border-line rounded-2xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:border-brand-200">
                <blockquote className="text-body-sm text-ink leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <div className="size-9 rounded-full bg-gradient-to-br from-brand-200 to-brand-700 text-white grid place-items-center font-bold text-[11px] shrink-0">
                    {t.initials}
                  </div>
                  <div className="min-w-0">
                    <div className="text-caption font-semibold text-ink truncate">{t.name}</div>
                    <div className="text-[11px] text-ink-subtle truncate">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>


      {/* ── BRAND ROW — light treatment with structure & weight ─── */}
      <section className="relative overflow-hidden bg-surface-alt border-t border-line">
        {/* Soft brand wash — gives the section warmth without darkness */}
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(204, 251, 241, 0.35) 0%, transparent 60%)" }} />
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-50" />

        <div className="relative max-w-site mx-auto px-6 lg:px-12 pt-20 pb-4">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow text-brand-700 mb-3">Brands we buy</div>
              <h3 className="font-brand text-display text-ink font-bold tracking-[-0.02em] leading-tight">
                {brands.length}+ retailers,<br className="lg:hidden" /> all paying in cash.
              </h3>
              <p className="mt-5 text-body text-ink-muted max-w-xl leading-relaxed">
                Open deals rotate weekly. Members reserve before the cap fills, submit before the deadline, and get paid by ACH on the published terms.
              </p>
            </div>
            <Link to="/deals" className="hidden sm:inline-flex items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800 group whitespace-nowrap">
              See all live deals
              <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          {/* Stat row — gives the section anchoring weight */}
          <dl className="grid grid-cols-3 gap-px bg-line border border-line rounded-2xl overflow-hidden mb-12 max-w-2xl">
            <BrandRowStat value={format.currencyCompact(trustIndicators.totalPaidOut)} label="Paid · last 30 days" />
            <BrandRowStat value={trustIndicators.brands} label="Brands live this week" />
            <BrandRowStat value={`${trustIndicators.averageRate}%`} label="Average rate" />
          </dl>
        </div>

        <div className="relative pb-20">
          {/* Edge fades */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-surface-alt via-surface-alt/80 to-transparent pointer-events-none z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-surface-alt via-surface-alt/80 to-transparent pointer-events-none z-10" />

          <div className="flex animate-marquee gap-3 w-max" style={{ animationDuration: "60s" }}>
            {[...brands, ...brands].map((b, i) => {
              const initial = b.name.split(" ").map((w) => w[0]).slice(0, 2).join("");
              return (
                <div
                  key={i}
                  className="group flex items-center gap-3 pl-2 pr-5 py-2 rounded-full bg-surface border border-line shadow-xs hover:shadow-md hover:border-brand-700/30 hover:-translate-y-0.5 transition-all whitespace-nowrap shrink-0"
                >
                  <div className="size-9 rounded-full bg-gradient-to-br from-brand-700 to-brand-900 ring-1 ring-surface grid place-items-center text-caption font-bold text-white shrink-0 group-hover:from-brand-600 group-hover:to-brand-800 transition-colors">
                    {initial}
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-body-sm font-semibold text-ink">{b.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-ink-hint">{b.category}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-ink via-brand-950 to-ink text-white">
        {/* Layers */}
        <div className="absolute inset-0 grain-texture pointer-events-none opacity-[0.20] mix-blend-overlay" />
        <div className="absolute inset-0 bg-stripe-dark pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(94,234,212,0.18), transparent)" }} />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(15,118,110,0.30), transparent)" }} />

        <div className="relative max-w-site mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-caption text-brand-200 uppercase tracking-wider mb-7">
              <Sparkles className="size-3.5 text-brand-300" /> Now accepting new applicants
            </div>
            <h2 className="font-brand text-display lg:text-display-lg leading-[1.04] text-white tracking-[-0.025em]">
              Ready to put your unused cards to work?
            </h2>
            <p className="mt-6 text-body lg:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Most applications are reviewed within two business days. There&rsquo;s no fee to apply, no minimum volume, and you can leave any time.
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:flex-col lg:items-end space-y-4">
            <Link
              to="/apply"
              data-testid="cta-apply-btn"
              className="group relative overflow-hidden inline-flex items-center gap-4 h-16 pl-9 pr-3 rounded-full bg-surface hover:bg-brand-50 text-ink font-brand text-body font-bold shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="relative z-10 whitespace-nowrap">Apply to join</span>
              <span className="relative z-10 size-10 rounded-full bg-brand-700 grid place-items-center text-white transition-transform group-hover:rotate-45 group-hover:bg-brand-800 shrink-0">
                <ArrowUpRight className="size-5" strokeWidth={2.4} />
              </span>
              <span className="absolute inset-0 -translate-x-full group-hover:animate-shimmer-x bg-gradient-to-r from-transparent via-brand-100/50 to-transparent pointer-events-none" />
            </Link>
            <div className="flex items-center gap-2 lg:justify-end text-caption text-slate-400">
              <Lock className="size-3.5" /> 2-minute application · No card required
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────── */

function BrandRowStat({ value, label }) {
  return (
    <div className="bg-surface p-5 lg:p-6">
      <div className="font-brand text-h1 text-ink font-bold tabular-nums tracking-[-0.02em] leading-none">{value}</div>
      <div className="mt-2 text-caption uppercase tracking-wider text-ink-subtle">{label}</div>
    </div>
  );
}

// Three short testimonials — one corporate / one freelance / one retail
// persona to cover the core archetypes without overloading the section.
const TESTIMONIALS = [
  {
    initials: "MR",
    name:     "Marcus R.",
    role:     "Spend ops lead, Austin",
    quote:    "Quarterly recognition cards used to sit in a drawer until Q4. GCBC moves them in days at rates I could never negotiate alone.",
  },
  {
    initials: "JW",
    name:     "Jennifer W.",
    role:     "Freelance designer, Brooklyn",
    quote:    "I averaged 89% last quarter — up from 78–82% on Raise. That difference covers my Adobe subscription twice over.",
  },
  {
    initials: "DH",
    name:     "David H.",
    role:     "Retired, Sarasota",
    quote:    "Three years of accumulated birthday cards turned into a real travel budget. ACH hits before the deal even closes.",
  },
];


const AVATARS = [
  { initials: "MR", bg: "bg-gradient-to-br from-brand-700 to-brand-900" },
  { initials: "AT", bg: "bg-gradient-to-br from-brand-600 to-brand-800" },
  { initials: "JC", bg: "bg-gradient-to-br from-brand-800 to-brand-950" },
  { initials: "SL", bg: "bg-gradient-to-br from-brand-700 to-brand-950" },
];

const TONE_MAP = {
  amber: { bg: "bg-surface",        border: "border-line",        text: "text-ink",       icon: "bg-gradient-to-br from-brand-700 to-brand-900" },
  info:  { bg: "bg-surface",        border: "border-line",        text: "text-brand-700", icon: "bg-gradient-to-br from-brand-600 to-brand-800" },
  brand: { bg: "bg-brand-50",     border: "border-brand-100",   text: "text-brand-800", icon: "bg-gradient-to-br from-brand-700 to-brand-950" },
};

function FloatingDealChip({ position, rotation, delay, brand, name, rate, tone }) {
  const t = TONE_MAP[tone];
  return (
    <div
      className={`absolute ${position} ${rotation} z-0 hidden md:block animate-float-slow`}
      style={{ animationDelay: delay }}
    >
      <div className={`${t.bg} border ${t.border} rounded-2xl p-3.5 pr-5 shadow-lg shadow-ink/[0.08] flex items-center gap-3 backdrop-blur-sm`}>
        <div className={`size-9 rounded-xl ${t.icon} text-white grid place-items-center text-caption font-bold shadow-inner`}>
          {brand}
        </div>
        <div>
          <div className="text-caption text-ink-subtle leading-tight">{name}</div>
          <div className={`font-brand text-h3 font-bold ${t.text} tabular-nums leading-none`}>{rate}<span className="text-caption">%</span></div>
        </div>
      </div>
    </div>
  );
}

function DarkStat({ label, value, highlight = false }) {
  return (
    <div>
      <div className="text-caption text-brand-200/70 uppercase tracking-wider">{label}</div>
      <div className={`mt-1 font-brand text-h2 font-bold tabular-nums ${highlight ? "text-brand-300" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div className="bg-surface p-7 group hover:bg-brand-50/40 transition-colors">
      <div className="flex items-center justify-between">
        <div className="size-11 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100 text-brand-700 grid place-items-center group-hover:scale-110 group-hover:rotate-3 transition-transform shadow-inner">
          {icon}
        </div>
        <div className="font-brand text-eyebrow text-ink-hint tabular-nums">{n}</div>
      </div>
      <h3 className="mt-5 text-h3 text-ink font-semibold">{title}</h3>
      <p className="mt-2 text-body-sm text-ink-muted leading-relaxed">{body}</p>
    </div>
  );
}

function CompareColumn({ kind, label, rate, points, highlight = false }) {
  // Use surface tokens (not white/60 overlays) so dark mode doesn't render
  // washed-out grey panels. Non-recommended columns stay recessed via
  // surface-alt + muted typography; the recommended column pops via the
  // full surface + brand strip + RECOMMENDED chip.
  const tones = {
    bad:  { mark: <X className="size-3" />,     rate_cls: "text-ink-subtle", header: "text-ink-subtle", strip: "bg-line"                                          },
    meh:  { mark: <Minus className="size-3" />, rate_cls: "text-ink",        header: "text-ink",        strip: "bg-line-strong"                                   },
    good: { mark: <Check className="size-3" />, rate_cls: "text-brand-700",  header: "text-brand-700",  strip: "bg-gradient-to-r from-brand-700 to-brand-300"     },
  }[kind];
  return (
    <div className={`relative p-7 transition-colors ${highlight ? "bg-surface" : "bg-surface-alt hover:bg-surface"}`}>
      <span className={`absolute top-0 left-0 right-0 h-1 ${tones.strip}`} />
      {highlight && (
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] font-bold text-brand-700 bg-brand-50 px-2 py-1 rounded">
          Recommended
        </span>
      )}
      <div className={`text-caption font-semibold uppercase tracking-wider ${tones.header}`}>{label}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className={`font-brand text-[40px] font-bold tabular-nums leading-none tracking-tight ${tones.rate_cls}`}>{rate}</span>
        <span className="text-caption text-ink-subtle">of face</span>
      </div>
      <ul className="mt-6 space-y-3">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2.5 text-body-sm">
            <span className={`size-4 rounded-full grid place-items-center mt-0.5 shrink-0 ${
              p.ok ? "text-brand-700 bg-brand-50" : "text-ink-hint bg-line/40"
            }`}>
              {p.ok ? <Check className="size-3" /> : tones.mark}
            </span>
            <span className={p.ok ? "text-ink" : "text-ink-muted"}>{p.t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
